import {
  Button,
  ChevronDownSVG,
  ChevronUpSVG,
  Chip,
  ChipGroup,
  Icon,
  useWindowSize,
} from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import { useEffect, useState } from "react";
import { useStandardsModalContext } from "@/search/NewSearchPageMVP/provider/StandardsModalProvider";
import { STANDARDS_SET_MODE } from "@/search/NewSearchPageMVP/types/IStandardsSetMode";
import { IStandard } from "@/search/NewSearchPageMVP/types/IStandard";
import {
  chipContainerCss,
  chipGroupCss,
  numberOfItemsCss,
  viewHideAllButtonCss,
  viewHideAllContainerCss,
  viewHideAllIconCss,
} from "./SelectedStandardsInSet.styles";

const ChipWithTrackEvent = withTrackEvent(Chip);
const SM_BREAKPOINT = 768;

const ButtonWithTrackEvent = withTrackEvent(Button);

export const SelectedStandardsInSet = () => {
  const {
    getters: {
      newStandardsSet: {
        id,
        title,
        children: standardsInNewSet,
        viewmodel_type,
      },
      confirmModalData: { editData },
      isPublishingStandardsSet,
      standardsSetMode,
    },
    handlers: { setNewStandardsSet, setConfirmModalData },
  } = useStandardsModalContext();
  const [showAllChips, setShowAllChips] = useState(false);
  const { width } = useWindowSize();

  const isEditMode = standardsSetMode === STANDARDS_SET_MODE.EDIT;
  const isCreateMode = standardsSetMode === STANDARDS_SET_MODE.CREATE;

  const [standardChipsToDisplay, setStandardChipsToDisplay] = useState<
    IStandard[]
  >([]);

  const handleRemoveSelectedStandard = (_: unknown, value: string) => {
    if (isEditMode && editData) {
      setConfirmModalData({
        setId: editData.id,
        editData: {
          ...editData,
          children: editData.children.filter((std) => std.content_id !== value),
        },
      });
      return;
    }

    if (isCreateMode && !isPublishingStandardsSet) {
      setNewStandardsSet({
        id,
        title,
        children: standardsInNewSet.filter(
          (standard) => standard.content_id !== value,
        ),
        viewmodel_type,
      });
    }
  };

  const handleToggleChips = () => {
    setShowAllChips(!showAllChips);
  };

  useEffect(() => {
    if (isEditMode && editData) {
      setStandardChipsToDisplay(editData?.children);
    }

    if (isCreateMode) {
      setStandardChipsToDisplay(standardsInNewSet);
    }
  }, [editData?.children, standardsInNewSet]);

  useEffect(() => {
    if (width > SM_BREAKPOINT && !showAllChips) {
      setShowAllChips(true);
    }
  }, [width, showAllChips]);

  const getChipsList = (standards: IStandard[]) => {
    return standards.map((standard, i) => (
      <ChipWithTrackEvent
        key={i}
        text={`${standard.display_fields.number_enhanced}`}
        value={standard.content_id}
        dataProps={{
          "data-qa-selector": `selected-standard-${standard.content_id}`,
          "data-testid": `selected-standard-${i}`,
        }}
        objectType={objectTypes.content}
        actionName={actionNames.delete}
        streamProperties={{
          description: "Remove standard from standard set",
        }}
        disabled={isPublishingStandardsSet}
      />
    ));
  };

  return (
    <ChipGroup
      size={ChipGroup.chipSizes.small}
      groupName="selectedStandards"
      onChipEvent={handleRemoveSelectedStandard}
      chipType={ChipGroup.chipTypes.input}
      __cssFor={{
        root: chipGroupCss,
      }}
    >
      <div css={numberOfItemsCss}>
        <span>
          {`${standardChipsToDisplay?.length} ${standardChipsToDisplay?.length === 1 ? "item" : "items"} in set:`}
        </span>
        <ButtonWithTrackEvent
          __cssFor={{ root: viewHideAllButtonCss }}
          onClick={handleToggleChips}
          flavor={Button.flavors.tertiary}
          size={Button.sizes.sm}
          actionName={actionNames.interact}
          objectType={objectTypes.content}
          streamProperties={{
            description: "Toggle visibility of the selected standards chips",
          }}
          dataProps={{
            "data-qa-selector":
              "standards-modal-toggle-standard-in-standard-set-button",
          }}
        >
          <span css={viewHideAllContainerCss}>
            {showAllChips ? "Hide all" : "View all"}
            <Icon
              __cssFor={{ root: viewHideAllIconCss }}
              title="Expand selected standards/Collapse standards"
              SvgComponent={showAllChips ? ChevronUpSVG : ChevronDownSVG}
            />
          </span>
        </ButtonWithTrackEvent>
      </div>
      {showAllChips && (
        <div css={chipContainerCss}>{getChipsList(standardChipsToDisplay)}</div>
      )}
    </ChipGroup>
  );
};
